import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import AccordionDetails from 'system/components/groups/form/accordion-details';
import { Accordion, Grid, Typography } from '@mui/material';
import TableOperatorServices from './table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { Service } from 'shell/models/service';
import { HttpResponseList } from 'shell/models/http-response';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { HeadCell } from 'shell/components/shared/table/types';
import { PmiButton } from 'shell/components/shared/button';
import { OperatorServicesVM } from 'system/view-models/services';
import { AxiosResponse } from 'axios';
import { ServicesForm } from 'system/view-models/operator';

const sortList = (list: Array<OperatorServicesVM>) =>
  list.sort((a, b) => {
    const aServices = a.services || '';
    const bServices = b.services || '';

    if (aServices < bServices) {
      return -1;
    }

    if ((a.services || '') < (b.services || '')) {
      return -1;
    }

    return 1;
  });

export const headServices: HeadCell<OperatorServicesVM, keyof OperatorServicesVM>[] = [
  {
    id: 'id',
    label: 'ID',
  },
  {
    id: 'services',
    label: 'Servizi',
  },
  {
    id: 'action',
    label: '',
  },
];

interface ServicesDetailProps {
  onChange: (key: keyof ServicesForm, value: ServicesForm[keyof ServicesForm]) => void;
  operatorServices: Array<OperatorServicesVM>;
  disabled?: boolean;
}

const ServicesComponent = ({
  onChange,
  operatorServices,
  disabled,
}: ServicesDetailProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(!false);

  const [services] = useHttpFetch<HttpResponseList<Service>, Array<OperatorServicesVM>>(
    `${__MANAGEMENT_API_URL__}/servizi`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: [],
        params: {
          sort: 'descrizione:ASC',
        },
      },
      transformer: (response: AxiosResponse<HttpResponseList<Service>>) => {
        const { data } = response;
        return data?.payload.list.map((item) => ({
          id: item.key.codiceServizio,
          services: item.descrizione,
        })) as Array<OperatorServicesVM>;
      },
    }
  );

  const servicesSaved: Array<OperatorServicesVM> = useMemo(() => {
    if (!services.data) {
      return [];
    }
    const list: Array<OperatorServicesVM> = ((services.data as Array<OperatorServicesVM>) || [])
      .map((element: OperatorServicesVM) => {
        const newService = {
          id: element.id,
          services: element.services,
        };

        const isSelectedService = !!operatorServices.find(
          (service) => service.id === newService.id.toString()
        );

        return {
          ...newService,
          checked: isSelectedService,
        };
      })
      .filter((item) => item.checked)
      .map((item) => ({ ...item, checked: false }));

    return sortList(list);
  }, [services, operatorServices]);

  const servicesNotSelected: Array<OperatorServicesVM> = useMemo(() => {
    if (!services.data) {
      return [];
    }

    const list: Array<OperatorServicesVM> = ((services.data as Array<OperatorServicesVM>) || [])
      .map((element: OperatorServicesVM) => {
        const newService = {
          id: element.id,
          services: element.services,
        };

        const isSelectedService = !!operatorServices.find(
          (service) => service.id === newService.id.toString()
        );

        return {
          ...newService,
          checked: isSelectedService,
        };
      })
      .filter((item) => !item.checked);

    return sortList(list);
  }, [services, operatorServices]);

  const [servicesSavedState, setServicesSavedState] =
    useState<Array<OperatorServicesVM>>(servicesSaved);

  const [servicesNotSelectedState, setServicesNotSelectedState] =
    useState<Array<OperatorServicesVM>>(servicesNotSelected);

  useEffect(() => {
    setServicesSavedState(servicesSaved);
  }, [servicesSaved]);

  useEffect(() => {
    setServicesNotSelectedState(servicesNotSelected);
  }, [servicesNotSelected]);

  const onCheckPressSaved = useCallback(
    (serviceSelected) => {
      const servicesNew = servicesSavedState.slice().map((service) =>
        service.id === serviceSelected.id
          ? {
              ...service,
              checked: !service.checked,
            }
          : service
      );

      setServicesSavedState(servicesNew);
    },
    [setServicesSavedState, servicesSavedState]
  );

  const onCheckPressNotSelected = useCallback(
    (serviceSelected) => {
      const servicesNew = servicesNotSelectedState.slice().map((service) =>
        service.id === serviceSelected.id
          ? {
              ...service,
              checked: !service.checked,
            }
          : service
      );

      setServicesNotSelectedState(servicesNew);
    },
    [setServicesNotSelectedState, servicesNotSelectedState]
  );

  const onSelectAllNotSelected = useCallback(
    (selected) => {
      const servicesNew = servicesNotSelectedState.slice().map((service) => ({
        ...service,
        checked: selected,
      }));

      setServicesNotSelectedState(servicesNew);
    },
    [setServicesNotSelectedState, servicesNotSelectedState]
  );

  const onSelectAllSaved = useCallback(
    (selected) => {
      const servicesNew = servicesSavedState.slice().map((service) => ({
        ...service,
        checked: selected,
      }));

      setServicesSavedState(servicesNew);
    },
    [setServicesSavedState, servicesSavedState]
  );

  const handleCheckedRight = useCallback(() => {
    const servicesNotSelectedOld = servicesNotSelectedState.slice().filter((item) => item.checked);

    const servicesSavedUpdated = servicesSavedState.concat(servicesNotSelectedOld);

    const newNotSelectedServices = servicesNotSelectedState.slice().filter((item) => !item.checked);

    setServicesNotSelectedState(sortList(newNotSelectedServices));

    setServicesSavedState(sortList(servicesSavedUpdated));

    onChange('servicesList', servicesSavedUpdated.map((item) => item.id) || []);
  }, [
    onChange,
    setServicesSavedState,
    setServicesNotSelectedState,
    servicesSavedState,
    servicesNotSelectedState,
  ]);

  const handleCheckedLeft = useCallback(() => {
    const servicesSavedOld = servicesSavedState.slice().filter((item) => item.checked);

    const servicesNotSelectedUpdated = servicesNotSelectedState.concat(servicesSavedOld);

    setServicesNotSelectedState(sortList(servicesNotSelectedUpdated));

    const newSavedServices = servicesSavedState.slice().filter((item) => !item.checked);

    setServicesSavedState(sortList(newSavedServices));

    onChange('servicesList', newSavedServices.map((item) => item.id) || []);
  }, [
    onChange,
    setServicesSavedState,
    setServicesNotSelectedState,
    servicesSavedState,
    servicesNotSelectedState,
  ]);

  return (
    <div className="pmi-pt-2">
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-groups-content"
          id="panel-groups-header"
          classes={{
            root: 'pmi-flex-row-reverse',
          }}
          expandIcon={<ExpandMore />}
        >
          <Typography component="h6">Lista Servizi</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1} justifyContent="center">
            <Grid item xs>
              <Typography component="h6">Disponibili</Typography>
              <TableOperatorServices
                minWidth={600}
                head={headServices}
                disabled={disabled}
                body={servicesNotSelectedState?.filter(() => !disabled) || []}
                onCheckPress={onCheckPressNotSelected}
                onSelectAll={onSelectAllNotSelected}
              />
            </Grid>
            <Grid item display="flex">
              <Grid container direction="column" justifyContent="center" display="flex">
                <PmiButton
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedRight}
                  disabled={servicesNotSelectedState.length === 0 || disabled}
                  aria-label="move selected right"
                >
                  &gt;
                </PmiButton>
                <PmiButton
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeft}
                  disabled={servicesSavedState.length === 0 || disabled}
                  aria-label="move selected left"
                >
                  &lt;
                </PmiButton>
              </Grid>
            </Grid>
            <Grid item xs>
              <Typography component="h6">Scelte</Typography>
              <TableOperatorServices
                minWidth={600}
                head={headServices}
                disabled={disabled}
                body={
                  servicesSavedState.map((item) => ({
                    ...item,
                    checked: disabled ? true : item.checked,
                  })) || []
                }
                onCheckPress={onCheckPressSaved}
                onSelectAll={onSelectAllSaved}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
ServicesComponent.displayName = 'Services component';

const ServicesTable = memo(ServicesComponent);

export default ServicesTable as typeof ServicesComponent;
